"use client";

import { useEffect, useId, useRef, useState } from "react";
import { useTranslations } from "next-intl";

import { Button } from "@/components/ui/button";
import { Link } from "@/i18n/navigation";

import { useConsent } from "./useConsent";

interface ConsentModalProps {
  open: boolean;
  onClose: () => void;
}

export function ConsentModal({ open, onClose }: ConsentModalProps) {
  if (!open) return null;
  // Inner component remounts each time `open` flips true, so the toggle's
  // initial state always reflects the latest persisted consent.
  return <ConsentModalContent onClose={onClose} />;
}

function ConsentModalContent({ onClose }: { onClose: () => void }) {
  const t = useTranslations("consent.modal");
  const { analytics, marketing, save } = useConsent();
  const [allowAnalytics, setAllowAnalytics] = useState<boolean>(analytics === "granted");
  const [allowMarketing, setAllowMarketing] = useState<boolean>(marketing === "granted");
  const titleId = useId();
  const descriptionId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKey);
    dialogRef.current?.focus();
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose]);

  const handleSave = () => {
    save({
      analytics: allowAnalytics ? "granted" : "denied",
      marketing: allowMarketing ? "granted" : "denied",
    });
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center bg-ink/40 p-4 sm:items-center"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
        className="w-full max-w-md rounded-2xl border border-paper-3 bg-paper p-6 text-ink shadow-[0_20px_40px_-10px_rgba(30,26,25,0.25)] outline-none"
      >
        <h2 id={titleId} className="font-heading text-2xl leading-tight tracking-tight">
          {t("title")}
        </h2>
        <p id={descriptionId} className="mt-3 text-sm text-ink-2">
          {t("description")}{" "}
          <Link
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            href={"/privacy" as any}
            className="underline underline-offset-2 hover:text-ink"
            onClick={onClose}
          >
            {t("policyLink")}
          </Link>
        </p>

        <div className="mt-5 flex flex-col gap-3">
          <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-paper-3 bg-paper-2/60 p-4">
            <input
              type="checkbox"
              checked={allowAnalytics}
              onChange={(event) => setAllowAnalytics(event.target.checked)}
              className="mt-1 size-4 cursor-pointer accent-ink"
            />
            <span className="flex flex-col gap-1">
              <span className="text-sm font-semibold">{t("analyticsLabel")}</span>
              <span className="text-xs text-ink-3">{t("analyticsHint")}</span>
            </span>
          </label>

          <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-paper-3 bg-paper-2/60 p-4">
            <input
              type="checkbox"
              checked={allowMarketing}
              onChange={(event) => setAllowMarketing(event.target.checked)}
              className="mt-1 size-4 cursor-pointer accent-ink"
            />
            <span className="flex flex-col gap-1">
              <span className="text-sm font-semibold">{t("marketingLabel")}</span>
              <span className="text-xs text-ink-3">{t("marketingHint")}</span>
            </span>
          </label>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} size="sm">
            {t("cancel")}
          </Button>
          <Button onClick={handleSave} size="sm">
            {t("save")}
          </Button>
        </div>
      </div>
    </div>
  );
}
